/**
 * @fileoverview Client-safe session validation utilities
 * 
 * @purpose Provides pure functions for session validation that can be safely used in client components.
 *          These functions don't require database access and are safe to bundle in client code.
 * 
 * @usage Import in client or server code:
 *        import { isSessionValid, invalidateUserSessions, isUserSessionInvalidated } from '@korvo/core/lib/session-validation'
 * 
 * @ownership Core infrastructure - session validation (client-safe)
 */

// In-memory store for invalidated user IDs
// In production, this should be replaced with Redis or a similar distributed cache
const invalidatedUsers = new Set<string>();

/**
 * Invalidate all sessions for a specific user
 * This should be called when a user is deleted or when security requires immediate session termination
 */
export function invalidateUserSessions(userId: string): void {
    invalidatedUsers.add(userId);
    console.log(`Sessions invalidated for user: ${userId}`);
}

/**
 * Check if a user's sessions have been invalidated
 */
export function isUserSessionInvalidated(userId: string): boolean {
    return invalidatedUsers.has(userId);
}

/**
 * Clear all invalidated user entries (useful for testing or maintenance)
 */
export function clearInvalidatedUsers(): void {
    invalidatedUsers.clear();
}

/**
 * Get list of invalidated user IDs (for debugging)
 */
export function getInvalidatedUsers(): string[] {
    return Array.from(invalidatedUsers);
}

/**
 * Invalidate sessions for multiple users at once
 */
export function invalidateMultipleUserSessions(userIds: string[]): void {
    userIds.forEach(userId => invalidateUserSessions(userId));
    console.log(`Sessions invalidated for ${userIds.length} users`);
}

/**
 * Security event: User account compromised or suspicious activity detected
 * This immediately invalidates all sessions for the user
 */
export function securityInvalidateUser(userId: string, reason: string): void {
    invalidateUserSessions(userId);
    console.warn(`SECURITY: Sessions invalidated for user ${userId}. Reason: ${reason}`);
}

/**
 * Check if a session is valid (not expired and has a real user ID)
 * This is a pure function that doesn't require database access
 */
export function isSessionValid(session: unknown): boolean {
    if (!session || typeof session !== 'object') {
        return false;
    }
    const sessionObj = session as Record<string, unknown>;
    const user = sessionObj.user;
    if (!user || typeof user !== 'object') {
        return false;
    }
    const userObj = user as Record<string, unknown>;
    // Check if session is expired
    if (sessionObj.expires && typeof sessionObj.expires === 'string') {
        if (new Date(sessionObj.expires) <= new Date()) {
            return false;
        }
    }
    // Check if user has a valid ID (not empty string)
    const userId = userObj.id;
    if (!userId || typeof userId !== 'string' || userId === '') {
        return false;
    }
    // Note: GUEST role is now valid (guests have same rights as users)
    // Only check for empty or invalid roles
    const userRole = userObj.role;
    if (!userRole || typeof userRole !== 'string' || userRole === '') {
        return false;
    }
    return true;
}
